import type {
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
  SincronizacionDeTerreno,
} from "@/lib/terreno";

import { horaDeSincronizacion } from "./sincronizaciones";

/* ---------------------------------------------------------------------------
   La lógica PURA del resaltado de sincronizaciones en las pestañas del editor.

   Las pestañas NUNCA esconden ni reordenan nada: solo marcan lo que llegó en las
   sincronizaciones elegidas. Acá se decide qué cuenta cada pestaña (su "unidad"),
   cuáles sincronizaciones aportaron y cómo se dice. El filtro que SÍ oculta vive
   en `sincronizaciones.ts` y es solo de la pestaña "Registro de terreno".
--------------------------------------------------------------------------- */

export type PestanaConBarra =
  | "general"
  | "descripcion"
  | "suelos"
  | "agua"
  | "maquinaria"
  | "instalaciones"
  | "construcciones"
  | "plantaciones";

export type UnidadDeLaBarra = "capturas" | "registros" | "equipos" | "instalaciones" | "plantaciones";

const PESTANAS_CON_BARRA: ReadonlySet<string> = new Set<PestanaConBarra>([
  "general",
  "descripcion",
  "suelos",
  "agua",
  "maquinaria",
  "instalaciones",
  "construcciones",
  "plantaciones",
]);

/** Si la pestaña lleva la barra de sincronizaciones (no: Terreno, legales ni históricos). */
export function esPestanaConBarra(pestana: string): pestana is PestanaConBarra {
  return PESTANAS_CON_BARRA.has(pestana);
}

const UNIDAD_DE_LA_PESTANA: Record<PestanaConBarra, UnidadDeLaBarra> = {
  general: "capturas",
  descripcion: "capturas",
  suelos: "capturas",
  construcciones: "capturas",
  agua: "registros",
  maquinaria: "equipos",
  instalaciones: "instalaciones",
  plantaciones: "plantaciones",
};

const UNIDADES: Record<UnidadDeLaBarra, { singular: string; plural: string; femenina: boolean }> = {
  capturas: { singular: "captura", plural: "capturas", femenina: true },
  registros: { singular: "registro", plural: "registros", femenina: false },
  equipos: { singular: "equipo", plural: "equipos", femenina: false },
  instalaciones: { singular: "instalación", plural: "instalaciones", femenina: true },
  plantaciones: { singular: "plantación", plural: "plantaciones", femenina: true },
};

/** "3 equipos", "1 instalación". */
export function textoDeUnidades(n: number, unidad: UnidadDeLaBarra): string {
  const u = UNIDADES[unidad];

  return `${n} ${n === 1 ? u.singular : u.plural}`;
}

/* ---- La selección ------------------------------------------------------- */

/** Lo que se resalta mientras nadie elige: todo lo nuevo. */
export function seleccionPorDefecto(sincronizaciones: SincronizacionDeTerreno[]): string[] {
  return sincronizaciones.filter((s) => s.nueva).map((s) => s.id);
}

/**
 * Las sincronizaciones resaltadas. `null` es "nadie eligió" (= las nuevas); un
 * arreglo, aunque vacío, es una elección manual. Un id que ya no existe se ignora.
 */
export function resaltadasDe(
  seleccion: string[] | null,
  sincronizaciones: SincronizacionDeTerreno[],
): Set<string> {
  const existentes = new Set(sincronizaciones.map((s) => s.id));

  return new Set((seleccion ?? seleccionPorDefecto(sincronizaciones)).filter((id) => existentes.has(id)));
}

/* ---- Qué cuenta cada pestaña -------------------------------------------- */

type IdDeSincronizacion = string | null | undefined;

function idsDelCampo(c: CampoDeTerreno, conUbicacionYPortada: boolean): IdDeSincronizacion[] {
  return [
    ...c.textos.map((t) => t.sincronizacion_id),
    ...c.audios.map((a) => a.sincronizacion_id),
    ...c.fotos.map((f) => f.sincronizacion_id),
    ...(conUbicacionYPortada ? [c.gps?.sincronizacion_id, c.portada?.sincronizacion_id] : []),
  ];
}

/** Un registro junta foto, texto y notas de voz; cada parte trae su sincronización. */
export function idsDelRegistro(r: RegistroDeTerreno): IdDeSincronizacion[] {
  return [
    r.foto ? r.foto.sincronizacion_id : undefined,
    r.texto !== null ? r.sincronizacion_id : undefined,
    ...r.audios.map((a) => a.sincronizacion_id),
  ];
}

function idsDelItem(i: ItemDeTerreno): IdDeSincronizacion[] {
  return [
    ...i.registros.flatMap(idsDelRegistro),
    ...Object.values(i.notas ?? {}).map((n) => n?.sincronizacion_id),
  ];
}

const sinVacios = (ids: IdDeSincronizacion[]): string[] =>
  ids.filter((id): id is string => typeof id === "string");

const CAMPOS_DE_LA_DESCRIPCION: ReadonlySet<string> = new Set(["descripcion_ubicacion", "descripcion"]);

/** Cada captura es un elemento con su sincronización; las de antes de las sincronizaciones no cuentan. */
function porCaptura(ids: IdDeSincronizacion[]): string[][] {
  return sinVacios(ids).map((id) => [id]);
}

function seccionesDe(registro: RegistroDeTerrenoDelInforme, pestana: SeccionDeTerreno["pestana"]) {
  return registro.secciones.filter((s) => s.pestana === pestana);
}

function capturasDeLaSeccion(s: SeccionDeTerreno, camposDeLaSeccion: CampoDeTerreno[]): IdDeSincronizacion[] {
  return [
    ...camposDeLaSeccion.flatMap((c) => idsDelCampo(c, true)),
    ...s.items.flatMap(idsDelItem),
    ...s.capturas.flatMap(idsDelRegistro),
  ];
}

export interface ElementosDeLaPestana {
  unidad: UnidadDeLaBarra;
  /** Un elemento por unidad (captura, equipo…): las sincronizaciones en que llegó. */
  elementos: string[][];
}

/**
 * Lo que la pestaña cuenta, con la sincronización de cada cosa. Los datos de
 * antes de las sincronizaciones (`null`) no son elemento de nadie: nunca se
 * resaltan ni aportan a la barra.
 */
export function elementosDeLaPestana(
  registro: RegistroDeTerrenoDelInforme | undefined,
  pestana: PestanaConBarra,
): ElementosDeLaPestana {
  const unidad = UNIDAD_DE_LA_PESTANA[pestana];
  if (!registro) return { unidad, elementos: [] };

  // «Descripción de la propiedad» vive en dos CAMPOS de la sección de
  // «Antecedentes generales», que es la pestaña de al lado.
  const secciones = seccionesDe(registro, pestana === "descripcion" ? "general" : pestana);

  switch (pestana) {
    case "descripcion":
      return {
        unidad,
        elementos: porCaptura(
          secciones.flatMap((s) =>
            s.campos.filter((c) => CAMPOS_DE_LA_DESCRIPCION.has(c.clave)).flatMap((c) => idsDelCampo(c, false)),
          ),
        ),
      };

    case "general":
      return {
        unidad,
        elementos: porCaptura(
          secciones.flatMap((s) =>
            capturasDeLaSeccion(
              s,
              s.campos.filter((c) => !CAMPOS_DE_LA_DESCRIPCION.has(c.clave)),
            ),
          ),
        ),
      };

    case "maquinaria":
    case "instalaciones":
    case "plantaciones":
      return {
        unidad,
        elementos: secciones
          .flatMap((s) => s.items)
          .map((i) => [...new Set(sinVacios(idsDelItem(i)))])
          .filter((ids) => ids.length > 0),
      };

    default:
      return {
        unidad,
        elementos: porCaptura(secciones.flatMap((s) => capturasDeLaSeccion(s, s.campos))),
      };
  }
}

/** En cuántos elementos aparece esa sincronización. */
export function cuantosPorSincronizacion(elementos: string[][], id: string): number {
  return elementos.filter((e) => e.includes(id)).length;
}

/** Cuántos elementos tocan alguna de las sincronizaciones resaltadas. */
export function cuantosResaltados(elementos: string[][], resaltadas: ReadonlySet<string>): number {
  return elementos.filter((e) => e.some((id) => resaltadas.has(id))).length;
}

/** Las sincronizaciones que aportaron algo a la pestaña, de la más vieja a la más nueva. */
export function sincronizacionesQueAportan(
  elementos: string[][],
  sincronizaciones: SincronizacionDeTerreno[],
): SincronizacionDeTerreno[] {
  const presentes = new Set(elementos.flat());

  return sincronizaciones.filter((s) => presentes.has(s.id));
}

/* ---- Una fila de la tabla ----------------------------------------------- */

export interface ResaltadoDeUnaFila {
  on: boolean;
  /** "10:32", "09:12": las horas de las sincronizaciones resaltadas, la más nueva primero. */
  horas: string[];
}

/**
 * Si la fila de un ítem se resalta: tiene capturas (o notas) de alguna
 * sincronización resaltada. Incluye filas que ya existían y recibieron algo.
 */
export function resaltadoDelItem(
  item: ItemDeTerreno | null | undefined,
  resaltadas: ReadonlySet<string>,
  sincronizaciones: SincronizacionDeTerreno[],
): ResaltadoDeUnaFila {
  if (!item) return { on: false, horas: [] };

  const suyas = new Set(sinVacios(idsDelItem(item)));
  const horas = [...sincronizaciones]
    .reverse()
    .filter((s) => suyas.has(s.id) && resaltadas.has(s.id))
    .map((s) => horaDeSincronizacion(s.en));

  return { on: horas.length > 0, horas };
}

/* ---- Lo que dice la barra ------------------------------------------------ */

export function textoDeLaBarra({
  resaltados,
  unidad,
  etiquetas,
}: {
  resaltados: number;
  unidad: UnidadDeLaBarra;
  etiquetas: string[];
}): string {
  if (resaltados === 0) return "Nada resaltado. Elija una o más sincronizaciones.";

  const u = UNIDADES[unidad];
  const resaltado = `${u.femenina ? "resaltada" : "resaltado"}${resaltados === 1 ? "" : "s"}`;

  return `${textoDeUnidades(resaltados, unidad)} ${resaltado} · ${etiquetas.join(" · ")}`;
}
